import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import { LockReset, CheckCircle } from '@mui/icons-material';
import { getApiBaseUrl } from '../api/config';

const PasswordResetConfirmPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  useEffect(() => {
    // Validate that uid and token are present
    if (!uid || !token) {
      setError('Ungültiger Reset-Link. Bitte fordern Sie einen neuen Link an.');
    }
  }, [uid, token]);

  const handleConfirmReset = async () => {
    setError('');
    setSuccess(false);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      const apiUrl = await getApiBaseUrl();
      const response = await fetch(`${apiUrl}/auth/password-reset/confirm/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, token, new_password: newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Fehler beim Zurücksetzen des Passworts. Der Link könnte abgelaufen sein.');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte überprüfen Sie die API-Verbindung.');
      console.error('Password reset confirm error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        <Card sx={{ width: '100%', boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
              {success ? (
                <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
              ) : (
                <LockReset sx={{ fontSize: 48, color: 'primary.main' }} />
              )}
            </Box>

            <Typography variant="h4" align="center" gutterBottom>
              {success ? 'Passwort zurückgesetzt!' : 'Neues Passwort setzen'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Ihr Passwort wurde erfolgreich zurückgesetzt!
                </Typography>
                <Typography variant="body2">
                  Sie werden in wenigen Sekunden zur Login-Seite weitergeleitet...
                </Typography>
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }} align="center">
                  Geben Sie Ihr neues Passwort ein.
                </Typography>

                <TextField
                  fullWidth
                  label="Neues Passwort"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  required
                  helperText="Mindestens 8 Zeichen"
                  disabled={loading || !uid || !token}
                  autoFocus
                />

                <TextField
                  fullWidth
                  label="Passwort bestätigen"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading || !uid || !token}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newPassword && confirmPassword) {
                      handleConfirmReset();
                    }
                  }}
                />

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/login')}
                    disabled={loading}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleConfirmReset}
                    disabled={loading || !newPassword || !confirmPassword || !uid || !token}
                    startIcon={loading && <CircularProgress size={20} />}
                  >
                    {loading ? 'Setze Passwort...' : 'Passwort ändern'}
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="caption">
                    <strong>Sicherheitshinweis:</strong> Dieser Link ist nur 1 Stunde gültig und kann nur einmal verwendet werden.
                  </Typography>
                </Alert>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default PasswordResetConfirmPage;
